import { useEffect, useMemo, useState } from "react";
import { http, apiError } from "@/lib/api";
import { Plus, Trash, X, CalendarBlank } from "@phosphor-icons/react";

const blankExercise = () => ({ name: "", sets: 3, reps: "8-12", weight_target: "", notes: "" });
const blankDay = () => ({ day_name: "Day 1", focus: "", exercises: [blankExercise()] });
const MAX_WEEKS = 12;

const cloneDays = (days) =>
  (days || []).map((d) => ({
    day_name: d.day_name || "",
    focus: d.focus || "",
    exercises: (d.exercises || []).map((ex) => ({ ...ex })),
  }));

export default function CoachPrograms() {
  const [programs, setPrograms] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await http.get("/coach/programs");
      setPrograms(data);
    } catch (e) {
      setErr(apiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this program? Existing assignments will be removed.")) return;
    try {
      await http.delete(`/coach/programs/${id}`);
      await load();
    } catch (e) { setErr(apiError(e)); }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="id-label mb-3">— Library —</div>
          <h1 className="id-h1">Programs</h1>
        </div>
        <button onClick={() => setShowCreate(true)} className="id-btn-primary" data-testid="new-program-button">
          <Plus size={16} weight="bold" /> New
        </button>
      </div>

      {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333] mb-6">{err}</div>}

      {programs.length === 0 ? (
        <div className="id-card text-center py-16" data-testid="empty-programs">
          <div className="id-label text-zinc-500 mb-2">No programs yet</div>
          <p className="text-zinc-400 font-manrope">Build your first program to assign to clients.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="programs-grid">
          {programs.map((p) => {
            const isMultiWeek = Array.isArray(p.weeks_plan) && p.weeks_plan.length > 1;
            const week1Days = isMultiWeek ? (p.weeks_plan[0]?.days || []) : (p.days || []);
            return (
              <div key={p.id} className="id-card" data-testid={`program-card-${p.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-oswald text-xl uppercase tracking-wide truncate">{p.name}</div>
                    <div className="text-xs text-zinc-500 mt-1 font-manrope flex items-center gap-2">
                      <span>{p.weeks} weeks · {week1Days.length} days/wk</span>
                      {isMultiWeek && (
                        <span className="text-[9px] font-manrope bg-[#FF3333]/10 text-[#FF3333] border border-[#FF3333]/40 px-1.5 py-0.5 uppercase tracking-[0.1em]" data-testid={`multi-week-badge-${p.id}`}>
                          Multi-week
                        </span>
                      )}
                    </div>
                  </div>
                  <button onClick={() => del(p.id)} className="text-zinc-600 hover:text-[#FF3333] p-1" data-testid={`delete-program-${p.id}`}>
                    <Trash size={18} weight="bold" />
                  </button>
                </div>
                {p.description && <p className="text-zinc-400 text-sm mt-3 font-manrope">{p.description}</p>}
                <div className="mt-4 pt-4 border-t border-[#262626] flex flex-wrap gap-2">
                  {week1Days.map((d, i) => (
                    <span key={i} className="id-label text-zinc-400 border border-[#262626] px-2 py-1">
                      {d.day_name}{d.focus ? ` · ${d.focus}` : ""}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && <CreateProgramModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
    </div>
  );
}

function CreateProgramModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [weeks, setWeeks] = useState(4);
  // weeksPlan: [{week, days: [...]}]. Editor always works per-week.
  const [weeksPlan, setWeeksPlan] = useState([{ week: 1, days: [blankDay()] }]);
  const [activeWeek, setActiveWeek] = useState(1);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  // Resize weeksPlan when `weeks` input changes. Growth clones the last week
  // (progressive-overload bootstrap); shrink drops trailing weeks.
  useEffect(() => {
    const n = Math.max(1, Math.min(MAX_WEEKS, Number(weeks) || 1));
    setWeeksPlan((cur) => {
      if (cur.length === n) return cur;
      if (cur.length > n) return cur.slice(0, n);
      const seed = cur[cur.length - 1]?.days || [blankDay()];
      const added = Array.from({ length: n - cur.length }, (_, i) => ({
        week: cur.length + i + 1,
        days: cloneDays(seed),
      }));
      return [...cur, ...added];
    });
    setActiveWeek((w) => Math.min(w, n));
  }, [weeks]);

  const activeDays = weeksPlan.find((w) => w.week === activeWeek)?.days || [];

  const patchActiveWeek = (updater) => {
    setWeeksPlan((cur) => cur.map((w) => (w.week === activeWeek ? { ...w, days: updater(w.days) } : w)));
  };
  const updateDay = (i, patch) => patchActiveWeek((days) => days.map((d, idx) => idx === i ? { ...d, ...patch } : d));
  const updateExercise = (di, ei, patch) => patchActiveWeek((days) => days.map((d, idx) =>
    idx !== di ? d : { ...d, exercises: d.exercises.map((ex, j) => j !== ei ? ex : { ...ex, ...patch }) }
  ));
  const addDay = () => patchActiveWeek((days) => [...days, { ...blankDay(), day_name: `Day ${days.length + 1}` }]);
  const removeDay = (i) => patchActiveWeek((days) => days.filter((_, idx) => idx !== i));
  const addExercise = (di) => patchActiveWeek((days) => days.map((d, idx) =>
    idx !== di ? d : { ...d, exercises: [...d.exercises, blankExercise()] }
  ));
  const removeExercise = (di, ei) => patchActiveWeek((days) => days.map((d, idx) =>
    idx !== di ? d : { ...d, exercises: d.exercises.filter((_, j) => j !== ei) }
  ));

  const copyFromWeek = (src) => {
    if (src === activeWeek) return;
    const source = weeksPlan.find((w) => w.week === src);
    if (!source) return;
    if (!window.confirm(`Overwrite Week ${activeWeek} with a copy of Week ${src}?`)) return;
    setWeeksPlan((cur) => cur.map((w) => (w.week === activeWeek ? { ...w, days: cloneDays(source.days) } : w)));
  };

  const save = async () => {
    setErr("");
    if (!name.trim()) { setErr("Program name required"); return; }
    setSaving(true);
    try {
      const sanitized = weeksPlan.map((w) => ({
        week: w.week,
        days: (w.days || []).map((d) => ({
          day_name: d.day_name || "Day",
          focus: d.focus || "",
          exercises: (d.exercises || [])
            .filter((e) => (e.name || "").trim())
            .map((e) => ({
              name: e.name.trim(),
              sets: Number(e.sets) || 3,
              reps: e.reps || "",
              weight_target: e.weight_target || "",
              notes: e.notes || "",
            })),
        })),
      }));
      await http.post("/coach/programs", {
        name: name.trim(),
        description,
        weeks: Number(weeks) || 4,
        days: sanitized[0]?.days || [],
        weeks_plan: sanitized,
      });
      onCreated();
    } catch (e) { setErr(apiError(e)); }
    finally { setSaving(false); }
  };

  const totalExercises = useMemo(
    () => activeDays.reduce((acc, d) => acc + (d.exercises || []).filter((e) => (e.name || "").trim()).length, 0),
    [activeDays]
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" data-testid="create-program-modal">
      <div className="bg-[#0A0A0A] border border-[#262626] w-full max-w-3xl my-8">
        <div className="flex items-center justify-between border-b border-[#262626] px-6 py-4">
          <div>
            <div className="id-label">— New Program —</div>
            <h2 className="font-oswald text-2xl uppercase tracking-wide mt-1">Build It</h2>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="close-program-modal">
            <X size={22} weight="bold" />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="id-label block mb-2">Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} className="id-input" placeholder="e.g. Push/Pull/Legs Hypertrophy" data-testid="program-name-input" />
            </div>
            <div>
              <label className="id-label block mb-2">Weeks</label>
              <input type="number" min={1} max={MAX_WEEKS} value={weeks} onChange={(e) => setWeeks(e.target.value)} className="id-input" data-testid="program-weeks-input" />
            </div>
          </div>
          <div>
            <label className="id-label block mb-2">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="id-input" placeholder="Goals, intent, notes..." data-testid="program-description-input" />
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <CalendarBlank size={14} weight="bold" className="text-[#FF3333]" />
              <div className="id-label">Week-by-Week</div>
            </div>
            <p className="text-zinc-500 text-xs font-manrope mb-3">
              Each week has its own exercises. Growing weeks clones the previous one so you have a starting point for progressive overload.
            </p>
            <div className="flex flex-wrap gap-1.5 mb-3" data-testid="week-tabs">
              {weeksPlan.map((w) => {
                const isActive = w.week === activeWeek;
                return (
                  <button
                    key={w.week}
                    type="button"
                    onClick={() => setActiveWeek(w.week)}
                    className={
                      "px-3 py-2 text-xs font-manrope uppercase tracking-[0.15em] border transition-colors " +
                      (isActive
                        ? "border-[#FF3333] bg-[#FF3333]/10 text-white"
                        : "border-[#262626] text-zinc-500 hover:text-white hover:border-zinc-500")
                    }
                    data-testid={`week-tab-${w.week}`}
                  >
                    Week {w.week}
                  </button>
                );
              })}
            </div>
            {weeksPlan.length > 1 && (
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <span className="id-label text-zinc-500">Bootstrap Week {activeWeek} from:</span>
                {weeksPlan.filter((w) => w.week !== activeWeek).map((w) => (
                  <button
                    key={w.week}
                    type="button"
                    onClick={() => copyFromWeek(w.week)}
                    className="text-[10px] font-manrope uppercase tracking-[0.15em] border border-[#262626] hover:border-[#FF3333] hover:text-[#FF3333] text-zinc-400 px-2 py-1"
                    data-testid={`copy-from-week-${w.week}`}
                  >
                    Copy Week {w.week}
                  </button>
                ))}
              </div>
            )}
            <div className="text-zinc-500 text-xs font-manrope mb-2">
              Week {activeWeek} · {activeDays.length} day{activeDays.length !== 1 ? "s" : ""} · {totalExercises} exercise{totalExercises !== 1 ? "s" : ""}
            </div>
          </div>

          {activeDays.map((day, di) => (
            <div key={di} className="border border-[#262626] p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3 flex-1">
                  <input value={day.day_name} onChange={(e) => updateDay(di, { day_name: e.target.value })} className="id-input max-w-[180px]" placeholder="Day name" data-testid={`day-name-${di}`} />
                  <input value={day.focus} onChange={(e) => updateDay(di, { focus: e.target.value })} className="id-input flex-1" placeholder="Focus (Push, Legs...)" data-testid={`day-focus-${di}`} />
                </div>
                {activeDays.length > 1 && (
                  <button onClick={() => removeDay(di)} className="ml-3 text-zinc-500 hover:text-[#FF3333]" data-testid={`remove-day-${di}`}>
                    <Trash size={18} weight="bold" />
                  </button>
                )}
              </div>

              <div className="space-y-2">
                {day.exercises.map((ex, ei) => (
                  <div key={ei} className="grid grid-cols-12 gap-2 items-center">
                    <input value={ex.name} onChange={(e) => updateExercise(di, ei, { name: e.target.value })} className="id-input col-span-12 md:col-span-4" placeholder="Exercise name" data-testid={`ex-name-${di}-${ei}`} />
                    <input type="number" min={1} value={ex.sets} onChange={(e) => updateExercise(di, ei, { sets: e.target.value })} className="id-input col-span-3 md:col-span-2" placeholder="Sets" data-testid={`ex-sets-${di}-${ei}`} />
                    <input value={ex.reps} onChange={(e) => updateExercise(di, ei, { reps: e.target.value })} className="id-input col-span-4 md:col-span-2" placeholder="Reps" data-testid={`ex-reps-${di}-${ei}`} />
                    <input value={ex.weight_target} onChange={(e) => updateExercise(di, ei, { weight_target: e.target.value })} className="id-input col-span-4 md:col-span-3" placeholder="Target weight" data-testid={`ex-weight-${di}-${ei}`} />
                    <button onClick={() => removeExercise(di, ei)} className="col-span-1 text-zinc-500 hover:text-[#FF3333] justify-self-end" data-testid={`remove-ex-${di}-${ei}`}>
                      <X size={18} weight="bold" />
                    </button>
                  </div>
                ))}
                <button onClick={() => addExercise(di)} className="id-btn-ghost text-xs" data-testid={`add-ex-${di}`}>
                  <Plus size={14} weight="bold" /> Add exercise
                </button>
              </div>
            </div>
          ))}

          <button onClick={addDay} className="id-btn-secondary w-full" data-testid="add-day-button">
            <Plus size={14} weight="bold" /> Add Workout Day to Week {activeWeek}
          </button>

          {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333]">{err}</div>}
        </div>

        <div className="border-t border-[#262626] px-6 py-4 flex items-center justify-end gap-3">
          <button onClick={onClose} className="id-btn-secondary" data-testid="cancel-program-button">Cancel</button>
          <button onClick={save} disabled={saving} className="id-btn-primary" data-testid="save-program-button">
            {saving ? "Saving..." : "Save Program"}
          </button>
        </div>
      </div>
    </div>
  );
}
